import { OrderOverviewRoot } from './OrderOverview.styles'
import {
  Order,
  OrderStatus,
  isShippedOrderEvent,
  isStatusEvent,
  OrderPeparingShipmentEvent,
  OrderType,
} from 'models/Order'
import { Stack } from 'components/Stack'
import { Text } from 'components/Text'
import Trans from 'next-translate/Trans'
import { useTranslation } from 'hooks/useTranslation'
import { TranslationKeys } from 'locales/translationKeys'
import { format } from 'date-fns'
import { Button } from './OrderOverview.styles'
import { truncateUUID } from 'utils/truncateUUID'
import Link from 'next/link'
import { ProgressBar } from './components/ProgressBar'
import { useState, useEffect } from 'react'
import { getTrackingLink } from 'features/User/hooks/useOrderSteps'

type OrderOverviewProps = {
  order: Order
}

type OrderStatusTranslationType = {
  [key in string]: TranslationKeys
}

export const OrderOverview = (props: OrderOverviewProps) => {
  const { order } = props
  const { t } = useTranslation()
  const [step, setStep] = useState(0)

  const OrderStatusTranslation: OrderStatusTranslationType = {
    PREPARING_SHIPMENT: 'my-account:order.status.preparing',
    SHIPPED: 'my-account:order.status.shipped',
    DELIVERED: 'my-account:order.status.delivered',
    CREATED: 'my-account:order.status.upcoming',
  }

  interface CardTitleValue {
    translation: TranslationKeys;
    value: string | undefined;
  }

  type CardTitleType = {
    [key: string]: CardTitleValue;
  }

  const CardTitle: CardTitleType = {
    TRIAL_BOX: {
      translation: 'my-account:order.card-title.meal-plan',
      value: order.petName
    },
    ONE_TIME: {
      translation: 'my-account:order.card-title.one-time',
      value: truncateUUID(order.orderId)
    },
    MEAL_PLAN: {
      translation: 'my-account:order.card-title.meal-plan',
      value: order.petName
    },
    PRODUCTS_SUBSCRIPTION: {
      translation: 'my-account:order.card-title.bulk-sub',
      value: order.subscriptionId && truncateUUID(order.subscriptionId)
    },
    MANUAL_ORDER: {
      translation: 'my-account:order.card-title.one-time',
      value: truncateUUID(order.orderId)
    }
  };

  const getCardTitle = (orderType: string) => {
    return CardTitle[orderType] || {
      translation: 'my-account:order.card-title.one-time', 
      value: truncateUUID(order.orderId) 
    };
  };

  const OrderDetail: React.FC = () => {
    const formatDate = (dateString: string | undefined) => {
      if (!dateString) return ''
      try {
        return format(new Date(dateString), 'MM/dd/yyyy')
      } catch {
        return ''
      }
    }

    const event = order.historic.events.find((event) => event.status === order.status)

    if (event?.status === OrderStatus.CREATED && order.type !== OrderType.ONE_TIME) {
      return (
        <Stack direction="column" gap="0" alignItems="flex-start">
          {event && (
            <Text weight="semiBold">
              <Trans
                i18nKey="my-account:order.billing-date"
                values={{ date: formatDate(event.occurredAt) }}
              />
            </Text>
          )}
          <Text>{t('my-account:order.notes.upcoming')}</Text>
          <Link href=
            {
              order.type === OrderType.PRODUCTS_SUBSCRIPTION ? `/user/bulk-subscriptions/${order.subscriptionId}` :  `/user/pets/${order.petId}` 
            } passHref>
            <Button size="regular" variant="primary">
              {t('my-account:order.triggers.subscription')}
            </Button>
          </Link>
        </Stack>
      )
    } else if (isStatusEvent<OrderPeparingShipmentEvent>(event, OrderStatus.PREPARING_SHIPMENT)) {
      return (
        <Stack direction="column" gap="0" alignItems="flex-start">
          <Text weight="semiBold">
            <Trans
              i18nKey="my-account:order.estimated-ship-date-v2"
              values={{ date: format(new Date(event.estimatedShipDate), 'MM/dd/yyyy') }}
            />
          </Text>
          {order.type !== 'ONE_TIME' && <Text weight="regular">{t('my-account:order.notes.preparing')}</Text>}
          <Link href={`/user/orders/${order.orderId}`} passHref>
            <Button size="regular" variant="primary">
              {t('my-account:order.triggers.order-detail')}
            </Button>
          </Link>
        </Stack>
      )
    } else if (isShippedOrderEvent(event)) {
      return (
        <Stack direction="column" gap="0" alignItems="flex-start">
          <Text weight="semiBold">
            <Trans
              i18nKey="my-account:order.order-number"
              values={{ orderNumber: truncateUUID(order.orderId) }}
            />
          </Text>
          <Text weight="semiBold">
            <Trans
              i18nKey="my-account:order.estimated-delivery-date"
              values={{ date: formatDate(event.estimatedDeliveryDate) }}
            />
          </Text>
          <Text weight="semiBold">
            <Trans i18nKey="my-account:order.carrier-v2" values={{ carrier: event.carrier }} />
          </Text>
          <a target="_blank" href={getTrackingLink(event)} rel="noopener noreferrer">
            <Text weight="semiBold" css={{ cursor: 'pointer' }}>
              <Trans
                i18nKey="my-account:order.tracking-v2"
                values={{ trackingNumber: event.trackingNumber }}
              />
            </Text>
          </a>
        </Stack>
      )
    } else if (event?.status === OrderStatus.DELIVERED) {
      return (
        <Stack direction="column" gap="0" alignItems="flex-start">
          <Text weight="semiBold">
            <Trans
              i18nKey="my-account:order.order-number"
              values={{ orderNumber: truncateUUID(order.orderId) }}
            />
          </Text>
          <Text weight="semiBold">
            <Trans
              i18nKey="my-account:order.delivery-date-v2"
              values={{ date: formatDate(event.occurredAt) }}
            /> 
          </Text>
          <Text>{t('my-account:order.notes.delivered')}</Text>
          {order.type !== OrderType.ONE_TIME && <Link href={
              order.type === OrderType.PRODUCTS_SUBSCRIPTION ? `/user/bulk-subscriptions/${order.subscriptionId}` :  `/user/pets/${order.petId}` 
            } passHref>
            <Button size="regular" variant="primary">
              {t('my-account:order.triggers.subscription')}
            </Button>
          </Link>}
        </Stack>
      )
    }

    return <Text>No event found</Text>
  }

  useEffect(() => {
    switch (order.status) {
      case OrderStatus.CREATED:
        setStep(1)
        break
      case OrderStatus.PREPARING_SHIPMENT:
        setStep(2)
        break
      case OrderStatus.SHIPPED:
        setStep(3)
        break
      case OrderStatus.DELIVERED:
        setStep(4)
        break
      default:
        setStep(0)
    }
  }, [order.status])

  return (
    <OrderOverviewRoot>
      <Stack data-testid={`order-id-${order.orderId}`} direction="column" alignItems="flex-start">
        <Stack direction="column" gap="2">
          <Text color="accent-black" weight="semiBold">
            <Trans
              i18nKey={getCardTitle(order.type).translation}
              values={{ value: getCardTitle(order.type).value }}
            />
          </Text>
          <Text size="h4" weight="semiBold">
            {t(OrderStatusTranslation[order.status])}
          </Text>
        </Stack>
        <Stack direction="column" gap="3" width="fullWidth">
          <ProgressBar step={step} />
          <OrderDetail />
        </Stack>
      </Stack>
    </OrderOverviewRoot>
  )
}
